import {
  DailyStats,
  Race,
  RecoveryMetrics,
  SleepMetrics,
  User,
  WorkoutData,
} from '../types';
import { localDate } from '../utils/date';

// Demo mode: a fictional athlete's day, rich enough to exercise everything
// the product does — races with priorities, a real training week, imperfect
// recovery, life context — without an account or a single database write.
// All dates are generated relative to "now" so the demo never goes stale.

const daysAgo = (days: number, hour = 7) => {
  const d = new Date(Date.now() - days * 86400000);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
};

const daysAhead = (days: number) => localDate(days);

export const DEMO_USER: User = {
  id: 'demo-user',
  email: 'demo@fitscript.app',
  name: 'Sam Rivera',
  notificationPreferences: { sms: false, inApp: true },
  fitnessGoals: {
    primary: 'endurance',
    secondary: 'strength',
    weeklyWorkouts: 5,
    targetIntensity: 'moderate',
  },
  activityPreferences: ['Running', 'Cycling', 'Yoga'],
  weeklyTrainingVolume: 6,
  acceptedTerms: true,
  trainingDays: ['mon', 'tue', 'thu', 'sat', 'sun'],
  typicalSessionMinutes: 60,
  birthYear: 1988,
  experienceLevel: 'intermediate',
  injuryNotes: 'Left calf gets tight after long runs',
  lifeContext: 'Busy job and two young kids — training happens early mornings',
  maxHr: 185,
  thresholdPace: '8:05 /mi',
};

export const DEMO_RACES: Race[] = [
  {
    id: 'demo-race-a',
    user_id: 'demo-user',
    name: 'Brooklyn Half Marathon',
    race_date: daysAhead(42),
    distance_miles: 13.1,
    priority: 'A',
    goal: 'Sub 1:45',
  },
  {
    id: 'demo-race-c',
    user_id: 'demo-user',
    name: 'Company 5K',
    race_date: daysAhead(13),
    distance_miles: 3.1,
    priority: 'C',
    goal: null,
  },
];

// An honest week: solid volume, yesterday was hard intervals.
export const DEMO_WORKOUTS: WorkoutData[] = [
  {
    id: 'demo-w1',
    type: 'Interval Run',
    duration: 45 * 60,
    intensity: 'high',
    timestamp: daysAgo(1, 6),
    distance: 6.2 * 1609.34,
    averageHeartRate: 162,
    calories: 540,
  },
  {
    id: 'demo-w2',
    type: 'Easy Ride',
    duration: 50 * 60,
    intensity: 'low',
    timestamp: daysAgo(2, 6),
    distance: 15 * 1609.34,
    averageHeartRate: 121,
    calories: 430,
  },
  {
    id: 'demo-w3',
    type: 'Long Run',
    duration: 95 * 60,
    intensity: 'moderate',
    timestamp: daysAgo(3, 7),
    distance: 11 * 1609.34,
    averageHeartRate: 148,
    calories: 1120,
  },
  {
    id: 'demo-w4',
    type: 'Yoga',
    duration: 30 * 60,
    intensity: 'low',
    timestamp: daysAgo(5, 6),
    calories: 110,
  },
  {
    id: 'demo-w5',
    type: 'Tempo Run',
    duration: 40 * 60,
    intensity: 'high',
    timestamp: daysAgo(6, 6),
    distance: 5 * 1609.34,
    averageHeartRate: 158,
    calories: 470,
  },
];

// Recovery with a story: decent sleep but HRV dipped after yesterday's
// intervals — a "light training OK" day, not a green light.
export const DEMO_SLEEP: SleepMetrics = {
  duration: Math.round(6.9 * 3600),
  quality: 71,
  readiness: 72,
  timestamp: daysAgo(0, 7),
};

export const DEMO_RECOVERY: RecoveryMetrics = {
  hrv: 38,
  restingHeartRate: 56,
  bodyTemperature: 0.1,
  timestamp: daysAgo(0, 7),
  readinessScore: 72,
};

const withHr = DEMO_WORKOUTS.filter((w) => w.averageHeartRate);

export const DEMO_STATS: DailyStats = {
  oura: { sleep: DEMO_SLEEP, recovery: DEMO_RECOVERY },
  strava: {
    workouts: DEMO_WORKOUTS,
    summary: {
      totalDuration: DEMO_WORKOUTS.reduce((s, w) => s + w.duration, 0),
      totalDistance: DEMO_WORKOUTS.reduce((s, w) => s + (w.distance ?? 0), 0),
      totalCalories: DEMO_WORKOUTS.reduce((s, w) => s + (w.calories ?? 0), 0),
      averageHeartRate:
        withHr.reduce((s, w) => s + (w.averageHeartRate ?? 0), 0) / withHr.length,
    },
  },
};
